import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { performSearch } from '../search/thunks/search.thunk';
import { selectResults } from '../search/store/search.selector';
import { SearchResult } from '../search/interfaces/search.interface';
import '../search/search-results';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { booleanConverter } from '../../common/converters';
import { appendSchemaToHead } from '../../common/utils/seo-schema-utils';
import './product-cards-pagination';
import { getProductUrl } from './utils/product-card.util';
import { mapProductCardData } from '../search/mapping/search-product-card.mapping';
import "./variations/k12-program-card.display";
import { CtaTypeLabel } from './product.interface';
import ProductCardSearchUtil from "./utils/product-card.search.util";

enum ComponentType {
  shared = 'shared',
  isolated = 'isolated',
}

@customElement('mhecomm-k12-program-cards')
export class ProductCards extends connect(store)(BaseComponent) {
  @property()
  cardType?: string;

  @property()
  businessUnit?: string;

  @property({ converter: booleanConverter })
  limitCard?: boolean;

  @property({ converter: booleanConverter })
  lazyLoadImage?: boolean = true;

  @property({ type: Number })
  numberofcards?: number;

  @property()
  orientation = 'vertical';

  @property({ type: String })
  cta = CtaTypeLabel.NO_CTA;

  @property({ converter: booleanConverter })
  showCopyright = false;

  @property({ converter: booleanConverter })
  showISBN = false;

  @property({ converter: booleanConverter })
  showGrades = false;

  @property({ converter: booleanConverter })
  showDescription = false;

  @property({ converter: booleanConverter })
  showBusinessUnitLabel = false;

  @property({ converter: booleanConverter })
  showProductType = false;

  @property({ converter: booleanConverter })
  isEditorMode?: boolean;

  @property({ type: Array })
  identifiers?: Array<string>;

  @property()
  authoredId?: string;

  @property()
  componentType?: ComponentType = ComponentType.isolated;

  pageSize = 10;

  @state()
  private results: Array<SearchResult> = [];

  @state()
  private start = 1;

  @state()
  private total = 0;

  private searchFilter;

  @property()
  sorting?: string;

  @property({ type: String })
  headingLevel = "h3";

  connectedCallback() {
    super.connectedCallback();
    this._search(this.start);
  }

  private async _search(page: number) {
    let response: any;
    if (this.identifiers) {
      const searchParameters = {
        programIdList: this.identifiers,
        catalog: 'SEG_PROGRAM',
        start: page,
        facets: false,
        pageLength: this.pageSize,
        filter: this.searchFilter
      };
      if (ProductCardSearchUtil.isSortAlphabetically(this.sorting)) {
        searchParameters['sortBy'] = "title_asc";
      }
      response = await store.dispatch(
        performSearch(searchParameters,false)
      );
    }
    if (this.componentType === ComponentType.isolated) {
      this.results = response?.hits || [];
    }
  }

  stateChanged(state: StoreState) {
    if (this.componentType === ComponentType.shared) {
      this.results = selectResults(state) || [];
    }
  }

  getSchema(results: SearchResult[]) {
    return {
      '@context': 'http://schema.org',
      '@type': 'ItemList',
      numberOfItems: this.total,
      itemListElement: results.map((result, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'ProductCollection',
            image: this.getCardImageUrl(result.document),
            name: result.document.title,
            description: result.document.description,
            url: getProductUrl(result.document),
          },
        };
      }),
    };
  }

  getCardImageUrl(product) {
    switch (product.type) {
      case 'SEGProductDocument':
      case 'SEGProgramDocument':
        switch (product.documentType) {
          case 'Programs':
            return getImageSrcUrl(product?.image);
        }
    }
    return '';
  }

  renderComp() {
    const limitedResults =
      this.limitCard && this.numberofcards ? this.results.slice(0, this.numberofcards) : this.results;
    appendSchemaToHead(this.getSchema(limitedResults), this.authoredId || '');
    const productCardsList = mapProductCardData(limitedResults, 'SEG_PROGRAM');
    if (ProductCardSearchUtil.isSortManually(this.sorting)) {
      ProductCardSearchUtil.sortResultsByManualType(productCardsList, this.identifiers, "programId");
    }
    const render = html`
      ${this.isEditorMode || productCardsList.length > 0 ? html` <slot name="header-parsys"></slot> ` : nothing}
      <div class="product-cards-container">
        <div class="product-card-row-${this.orientation} mb-3">
          ${productCardsList.map(
            (item) => html`<mhecomm-k12-program-card-display
              .product="${item}"
              cta="${this.cta}"
              showBusinessUnitLabel="${this.showBusinessUnitLabel}"
              showBusinessLabelType="${this.showProductType}"
              showCopyright="${this.showCopyright}"
              showGrades="${this.showGrades}"
              showDescription="${this.showDescription}"
              orientation="${this.orientation}"
              headingLevel="${this.headingLevel}"
            >
            </mhecomm-k12-program-card-display>`
          )}
        </div>
      </div>
      <mhecomm-product-cards-pagination
        start="${this.start}"
        total="${this.total}"
        @onPageChange=${(e) => this._search(e.detail?.page)}
      >
      </mhecomm-product-cards-pagination>
      ${this.isEditorMode || limitedResults.length > 0 ? html` <slot name="footer-parsys"></slot> ` : nothing}
    `;
    return render;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-k12-program-cards': ProductCards;
  }
}
